/**
 * `/guide` — how to actually play, for a visitor who has never opened a browser FPS.
 *
 * Unlike FAQ, News and the legal documents this page has no CMS endpoint behind it, and that is
 * deliberate: **a guide that drifts from the game is worse than no guide.** So every number on
 * it is imported rather than typed — the mode rules from `MODE_CARDS`, the netcode figures from
 * `NETCODE_FACTS` (which are themselves `shared/src/protocol.ts` constants), the arsenal size
 * and unlock ladder from `shared/src/arsenal.ts`, the loadout shape from `LOADOUT_SLOT_COUNT`.
 * Retune the ladder or add a gun and this page is already right; an admin-authored copy of it
 * would have been wrong by the next balance pass.
 *
 * The controls section states both input schemes because both ship (CLAUDE.md, the 2026-09-04
 * touch decision): keyboard and mouse, and the touch layout `touch-controls.ts` mounts. Weapon
 * select is documented as absolute — 1/2/3 pick a slot, they never cycle — because that is the
 * protocol's own rule and a player who expects a cycle will fight the game.
 *
 * Anchored sections plus a table of contents that is a sticky rail on desktop and a scrolling
 * chip strip on mobile, so the page is navigable at 375px without a page-length scroll.
 */
import { LOADOUT_SLOT_COUNT, FREE_LOADOUT_SLOTS, WEAPON_UNLOCK } from '../../../../shared/src/arsenal.js';
import { SKIN_LIST } from '../../../../shared/src/cosmetics.js';
import { WEAPONS } from '../../../../shared/src/weapons.js';
import { MAX_PLAYERS } from '../../../../shared/src/protocol.js';
import { t } from '../i18n';
import { icon, plateChrome, type IconName, type PlateChromeOptions } from '../shell/ui';
import { landingFooter, landingTopBar, MODE_CARDS, NETCODE_FACTS, pageTitle } from './landing';

type Navigate = (to: string) => void;

const H1 = 'kit-title font-heading uppercase text-[24px] sm:text-[36px] lg:text-[46px] m-0';
const H2 = 'kit-title font-heading uppercase text-[18px] sm:text-[22px] m-0';
const LEDE = 'text-[15px] sm:text-[17px] leading-relaxed text-text-dim max-w-[62ch] m-0';
const BODY = 'text-[15px] leading-relaxed text-text-dim max-w-[70ch] m-0';
const CTA_PRIMARY: PlateChromeOptions = { variant: 'gold', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };
const CTA_GHOST: PlateChromeOptions = { variant: 'secondary', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function plateLink(href: string, label: string | Node, opts: PlateChromeOptions, navigate: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  plateChrome(a, label, { ...opts, className: `no-underline ${opts.className ?? ''}` });
  a.addEventListener('click', (event) => {
    event.preventDefault();
    navigate(href);
  });
  return a;
}

// ── Figures, derived from the code that owns them ───────────────────────────────────────────

const GUN_COUNT = WEAPONS.filter((weapon) => !weapon.thrown).length;
const THROWABLE_COUNT = WEAPONS.filter((weapon) => weapon.thrown).length;

/** The unlock ladder's ends: the first level that grants a weapon beyond the default three, and
 * the last. No price any more — reaching the level is the whole rule
 * (`design/economy/economy-rebuild.md` §3). */
function unlockLadder(): { firstLevel: number; lastLevel: number } {
  const gated = Object.values(WEAPON_UNLOCK).filter(
    (unlock): unlock is { kind: 'level'; level: number } => unlock.kind === 'level',
  );
  const levels = gated.map((unlock) => unlock.level);
  const firstLevel = levels.length ? Math.min(...levels) : 2;
  return {
    firstLevel,
    lastLevel: levels.length ? Math.max(...levels) : firstLevel,
  };
}

/** A keyboard row. The caption is a physical key and stays exactly as it is printed on one. */
interface ControlRow {
  keys: string;
  actionKey: string;
}

/** A touch row. Both halves are words rather than keycaps, so both are translated. */
interface TouchRow {
  keysKey: string;
  actionKey: string;
}

const KEYBOARD_CONTROLS: ControlRow[] = [
  { keys: 'W A S D', actionKey: 'guide.controls.keyboard.move' },
  { keys: 'Mouse', actionKey: 'guide.controls.keyboard.look' },
  { keys: 'Left mouse', actionKey: 'guide.controls.keyboard.fire' },
  { keys: 'Right mouse', actionKey: 'guide.controls.keyboard.ads' },
  { keys: '1 / 2 / 3', actionKey: 'guide.controls.keyboard.slots' },
  { keys: 'R', actionKey: 'guide.controls.keyboard.reload' },
  { keys: 'Space', actionKey: 'guide.controls.keyboard.jump' },
  { keys: 'Ctrl / C', actionKey: 'guide.controls.keyboard.crouch' },
  { keys: 'Tab', actionKey: 'guide.controls.keyboard.scoreboard' },
  { keys: 'Esc', actionKey: 'guide.controls.keyboard.pause' },
];

const TOUCH_CONTROLS: TouchRow[] = [
  { keysKey: 'guide.controls.touch.stick.keys', actionKey: 'guide.controls.touch.stick.action' },
  { keysKey: 'guide.controls.touch.look.keys', actionKey: 'guide.controls.touch.look.action' },
  { keysKey: 'guide.controls.touch.fire.keys', actionKey: 'guide.controls.touch.fire.action' },
  { keysKey: 'guide.controls.touch.jump.keys', actionKey: 'guide.controls.touch.jump.action' },
  { keysKey: 'guide.controls.touch.reload.keys', actionKey: 'guide.controls.touch.reload.action' },
  { keysKey: 'guide.controls.touch.landscape.keys', actionKey: 'guide.controls.touch.landscape.action' },
];

interface Section {
  id: string;
  art: IconName;
  navLabelKey: string;
  titleKey: string;
  build: (navigate: Navigate) => Node[];
}

/** The prose of a section, set on one painted plate rather than loose on the page. */
function paragraphs(...lines: string[]): Node[] {
  const plate = el('div', 'kit-plate [--kp-rim:18px] [--kp-pad:18px_20px] flex flex-col gap-3');
  plate.append(...lines.map((line) => el('p', BODY, line)));
  return [plate];
}

/** A key / action table: the key is a painted keycap (`kit-chip`), each row a recess in the plate. */
function definitionGrid(rows: [keys: string, action: string][], caption: string, art: IconName): HTMLElement {
  const block = el('div', 'kit-plate [--kp-rim:18px] [--kp-pad:16px_16px] flex flex-col gap-3');
  const head = el('div', 'flex items-center gap-2');
  head.append(icon(art, { size: 36 }), el('p', 'kit-label font-heading uppercase text-[13px] m-0', caption));
  const list = el('dl', 'flex flex-col gap-2 m-0');
  for (const [keys, action] of rows) {
    const row = el('div', 'kit-well flex items-center gap-3 px-3 py-2');
    const cap = el('dt', 'kit-chip [--kh:32px] shrink-0 min-w-[96px] sm:min-w-[130px] justify-center whitespace-normal text-center font-heading uppercase tracking-[0.06em] text-[12px] m-0', keys);
    row.append(cap, el('dd', 'text-[14px] leading-snug text-text m-0', action));
    list.append(row);
  }
  block.append(head, list);
  return block;
}

const SECTIONS: Section[] = [
  {
    id: 'what',
    art: 'star',
    navLabelKey: 'guide.sections.what.nav',
    titleKey: 'guide.sections.what.title',
    build: () => [
      ...paragraphs(
        t('guide.what.p1'),
        t('guide.what.p2', { players: MAX_PLAYERS }),
        t('guide.what.p3', { guns: GUN_COUNT, throwables: THROWABLE_COUNT }),
      ),
    ],
  },
  {
    id: 'modes',
    art: 'trophy',
    navLabelKey: 'guide.sections.modes.nav',
    titleKey: 'guide.sections.modes.title',
    build: () => {
      const nodes: Node[] = paragraphs(t('guide.modes.intro'));
      const grid = el('div', 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3');
      for (const mode of MODE_CARDS) {
        const card = el('article', 'kit-plate [--kp-rim:18px] [--kp-pad:18px_16px] flex flex-col gap-3');
        const head = el('div', 'flex items-center gap-3');
        head.append(
          icon(mode.icon, { size: 56, className: 'w-12 h-12' }),
          el('h3', 'kit-title font-heading uppercase text-[18px] m-0 flex-1', t(mode.nameKey)),
        );
        const figure = el('div', 'kit-well flex items-baseline gap-3 px-3 py-1');
        figure.append(
          el('span', 'kit-title font-heading text-[32px] leading-none', mode.figure),
          el('span', 'font-heading uppercase tracking-[0.16em] text-[11px] text-text-dim', t(mode.figureLabelKey)),
        );
        const rows = el('dl', 'flex flex-col gap-1.5 m-0');
        for (const [labelKey, valueKey] of mode.rows) {
          const row = el('div', 'kit-well grid grid-cols-[84px_minmax(0,1fr)] items-baseline gap-x-3 px-3 py-1.5');
          row.append(
            el('dt', 'font-heading uppercase tracking-[0.14em] text-[10px] text-text-dim m-0', t(labelKey)),
            el('dd', 'text-[13px] leading-snug text-text m-0', t(valueKey)),
          );
          rows.append(row);
        }
        card.append(head, figure, rows);
        grid.append(card);
      }
      nodes.push(grid);
      nodes.push(el('p', BODY, t('guide.modes.practiceNote')));
      return nodes;
    },
  },
  {
    id: 'controls',
    art: 'play',
    navLabelKey: 'guide.sections.controls.nav',
    titleKey: 'guide.sections.controls.title',
    build: () => [
      ...paragraphs(t('guide.controls.intro')),
      definitionGrid(
        KEYBOARD_CONTROLS.map((row) => [row.keys, t(row.actionKey)]),
        t('guide.controls.keyboardCaption'),
        'settings',
      ),
      definitionGrid(
        TOUCH_CONTROLS.map((row) => [t(row.keysKey), t(row.actionKey)]),
        t('guide.controls.touchCaption'),
        'play',
      ),
      el('p', BODY, t('guide.controls.absolute')),
    ],
  },
  {
    id: 'progression',
    art: 'rank',
    navLabelKey: 'guide.sections.progression.nav',
    titleKey: 'guide.sections.progression.title',
    build: (navigate) => {
      const ladder = unlockLadder();
      const nodes: Node[] = paragraphs(
        t('guide.progression.p1', { slots: LOADOUT_SLOT_COUNT, free: FREE_LOADOUT_SLOTS }),
        t('guide.progression.p2', {
          firstLevel: ladder.firstLevel,
          lastLevel: ladder.lastLevel,
        }),
        t('guide.progression.p3', { operators: SKIN_LIST.length }),
      );
      const steps = el('ol', 'flex flex-col gap-3 p-0 m-0 list-none');
      ['step1', 'step2', 'step3', 'step4'].forEach((step, index) => {
        const item = el('li', 'kit-plate [--kp-rim:16px] [--kp-pad:10px_14px] flex items-center gap-3 text-[15px] leading-snug text-text');
        item.append(el('span', 'kit-chip [--kh:40px] justify-center min-w-[40px] shrink-0 kit-label font-heading text-[18px]', String(index + 1)), t(`guide.progression.${step}`));
        steps.append(item);
      });
      nodes.push(steps);
      const cta = el('div', 'flex flex-wrap gap-3 pt-2');
      cta.append(
        plateLink('/register', t('landing.closing.createAccount'), CTA_PRIMARY, navigate),
        plateLink('/login', t('landing.closing.haveOne'), CTA_GHOST, navigate),
      );
      nodes.push(cta);
      return nodes;
    },
  },
  {
    id: 'netcode',
    art: 'time',
    navLabelKey: 'guide.sections.netcode.nav',
    titleKey: 'guide.sections.netcode.title',
    build: () => {
      const nodes: Node[] = paragraphs(t('guide.netcode.p1'), t('guide.netcode.p2'));
      const grid = el('div', 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4');
      for (const fact of NETCODE_FACTS) {
        const tile = el('div', 'kit-plate [--kp-rim:16px] [--kp-pad:14px] flex flex-col gap-1');
        const figure = el('p', 'kit-title font-heading text-[28px] leading-none m-0', fact.value);
        figure.append(el('span', 'kit-label font-heading text-[13px] ml-1', fact.unit));
        tile.append(figure);
        tile.append(
          el('p', 'kit-label font-heading uppercase text-[11px] m-0', t(fact.labelKey)),
        );
        tile.append(el('p', 'text-[13px] leading-snug text-text-dim m-0', t(fact.noteKey)));
        grid.append(tile);
      }
      nodes.push(grid);
      return nodes;
    },
  },
];

/**
 * A table-of-contents entry scrolls; it never navigates.
 *
 * `main.ts` intercepts every same-origin anchor click at the document and routes
 * `pathname + search` — the hash is dropped, so a bare `href="#modes"` remounted /guide at the
 * top of the page and the section never came into view (black-line.ink 06.10.2026, item 5).
 * The handler stops that, scrolls the section in, and writes the hash so the address bar and
 * a copied link name the section the reader is looking at.
 */
function tocLink(section: Section, reducedMotion: boolean): HTMLAnchorElement {
  const a = el(
    'a',
    'kit-tab [--kh:44px] snap-start shrink-0 lg:w-full lg:justify-start px-3 font-heading uppercase tracking-[0.1em] text-[12px] no-underline whitespace-nowrap',
    t(section.navLabelKey),
  );
  a.href = `#${section.id}`;
  a.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const target = document.getElementById(section.id);
    if (!target) return;
    target.classList.add('is-in');
    try {
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', `#${section.id}`);
    } catch {
      /* jsdom */
    }
  });
  return a;
}

function tableOfContents(reducedMotion: boolean): HTMLElement {
  const wrap = el('div', 'lg:sticky lg:top-28');
  wrap.dataset.slot = 'guide-toc';
  const inner = el('div', 'kit-plate [--kp-rim:16px] [--kp-pad:14px] flex flex-col gap-2');
  inner.append(el('p', 'kit-label font-heading uppercase text-[12px] m-0', t('guide.onThisPage')));
  const nav = el(
    'nav',
    // A chip strip that scrolls inside itself on mobile — the page must not scroll sideways.
    'flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible snap-x pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
  );
  nav.setAttribute('aria-label', t('guide.tocLabel'));
  for (const section of SECTIONS) nav.append(tocLink(section, reducedMotion));
  inner.append(nav);
  wrap.append(inner);
  return wrap;
}

export function renderGuidePage(app: HTMLElement, navigate: Navigate): () => void {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const root = el('div', 'landing relative min-h-full bg-bg text-text overflow-x-hidden');
  root.dataset.view = 'guide';

  const main = el(
    'main',
    'relative px-5 sm:px-8 lg:px-14 xl:px-24 pt-[calc(96px+env(safe-area-inset-top))] pb-20 lg:pb-28 max-w-[1280px] mx-auto',
  );

  const head = el('header', 'flex flex-col gap-4 mb-10 lg:mb-14');
  head.append(pageTitle('quests', el('h1', H1, t('guide.title'))), el('p', LEDE, t('guide.lede')));
  main.append(head);

  // `minmax(0,1fr)` on a phone too: the implicit `auto` column grew to the table of contents'
  // unwrapped chip strip and pushed the prose past the right edge, where it was clipped.
  const layout = el('div', 'grid gap-8 lg:gap-12 grid-cols-[minmax(0,1fr)] lg:grid-cols-[220px_minmax(0,1fr)]');
  layout.append(tableOfContents(reducedMotion));

  const column = el('div', 'flex flex-col gap-12 lg:gap-16 min-w-0');
  column.dataset.slot = 'guide-body';
  for (const section of SECTIONS) {
    const block = el('section', 'reveal flex flex-col gap-4 scroll-mt-28');
    block.id = section.id;
    const heading = el('div', 'kit-header [--kh:48px] w-fit max-w-full');
    heading.append(icon(section.art, { size: 40, className: 'w-8 h-8' }), el('h2', H2, t(section.titleKey)));
    block.append(heading);
    block.append(...section.build(navigate));
    column.append(block);
  }
  layout.append(column);
  main.append(layout);

  root.append(landingTopBar(navigate), main, landingFooter(navigate));
  app.replaceChildren(root);
  try {
    window.scrollTo(0, 0);
  } catch {
    /* jsdom */
  }

  let observer: IntersectionObserver | null = null;
  if (!reducedMotion && typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    for (const node of column.querySelectorAll<HTMLElement>('.reveal')) observer.observe(node);
  } else {
    for (const node of column.querySelectorAll<HTMLElement>('.reveal')) node.classList.add('is-in');
  }

  // A hash on arrival is a deep link into a section — honour it once the sections exist.
  if (window.location.hash) {
    const id = window.location.hash.replace(/^#/, '');
    const target = SECTIONS.some((section) => section.id === id) ? root.querySelector<HTMLElement>(`#${id}`) : null;
    target?.classList.add('is-in');
    try {
      target?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    } catch {
      /* jsdom */
    }
  }

  return () => {
    observer?.disconnect();
    root.remove();
  };
}
